import {Color} from '@benchling/nucleus-ui';
import {Container} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import reactUpdate from 'immutability-helper';
import React, {memo, useRef, useCallback, useMemo} from 'react';
import {v4 as uuidv4} from 'uuid';

import * as Analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {ReactionComponent} from 'chemistry/ChemistryConstants';
import {parseReactionComponents} from 'chemistry/ChemistryDAO';
import {
  COMMON_ANALYTICS_ACTIONS,
  AnalyticsModalType,
  AnalyticsMetadataForContext,
} from 'constants/AnalyticsConstants';
import {ActionOrigin, ActionMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ChemistryReactionRole,
  CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME,
  CHEMISTRY_REACTION_ROLE_TO_SORT_ORDER,
} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import EmptySchemePlaceholder from 'notebook/integrations/chemistry/reaction-scheme/EmptySchemePlaceholder';
import ReactionSchemeContent from 'notebook/integrations/chemistry/reaction-scheme/ReactionSchemeContent';
import ReactionSketcherModal, {
  StructureWithFormat,
} from 'notebook/integrations/chemistry/reaction-scheme/ReactionSketcherModal';
import {
  getMoleculeIdFromEvaluatedCell,
  getRoleFromEvaluatedCell,
  getColumnIndices,
} from 'notebook/integrations/chemistry/reaction-scheme/StoichiometryTableHelpers';
import useSyncReactionComponentsFromTable from 'notebook/integrations/chemistry/reaction-scheme/useSyncReactionComponentsFromTable';
import {
  StoichiometryStructuredTable,
  StoichiometryTableRow,
  StoichiometryTableCustomSlotProps,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import getBlankRow from 'notebook/structured-tables/util/getBlankRow';
import {EvaluatedTable} from 'notebook/tables/excess/Evaluator';
import {BaseTypedValueType} from 'notebook/tables/TableConstants';
// @ts-expect-error
import EntryStore from 'stores/EntryStore';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import * as net from 'util/Net';
import {withLoadingScreen} from 'util/Promises';
import sortedBy from 'util/sortedBy';

async function handleReactionSketcherSubmit(
  mrvStructure: string,
  folderId: string,
  structuredTable: StoichiometryStructuredTable,
  computedTable: EvaluatedTable,
  updateHandler: (structuredTable: StoichiometryStructuredTable) => void
): Promise<void> {
  const {externalFile, reactants, products} = await parseReactionComponents(mrvStructure, folderId);

  const rows = mergeReactionComponentsWithExistingRows(
    reactants,
    products,
    structuredTable.rows,
    structuredTable.columns,
    computedTable
  );

  updateHandler(
    reactUpdate(structuredTable, {
      metadata: {
        $merge: {
          reactionMrvExternalFile: {
            id: externalFile.id,
            url: externalFile.url,
          },
          syncedComponents: {
            [ChemistryReactionRole.REACTANT]: reactants.map((reactant) => reactant.moleculeId),
            [ChemistryReactionRole.PRODUCT]: products.map((product) => product.moleculeId),
          },
        },
      },
      rows: {$set: rows},
    })
  );
}

function mergeReactionComponentsWithExistingRows(
  newReactants: Array<ReactionComponent>,
  newProducts: Array<ReactionComponent>,
  existingRows: Array<StoichiometryTableRow>,
  columns: Array<StructuredTableColumn>,
  computedTable: EvaluatedTable
): Array<StoichiometryTableRow> {
  const {compoundColumnIndex, roleColumnIndex} = getColumnIndices(columns);

  const makeRowKey = (moleculeId: string, role: ChemistryReactionRole): string => `${moleculeId}|${role}`;

  const existingRowsByKey: Record<string, StoichiometryTableRow> = {};
  const preservedRows: Array<StoichiometryTableRow> = [];
  const evalutedRoleByRowId: Record<string, ChemistryReactionRole> = {};

  for (let i = 0; i < existingRows.length; i++) {
    const row = existingRows[i];
    const evaluatedRow = computedTable[i];
    const moleculeId = getMoleculeIdFromEvaluatedCell(evaluatedRow[compoundColumnIndex]);
    const role = getRoleFromEvaluatedCell(evaluatedRow[roleColumnIndex]);

    if (role != null) {
      // Keep track of role for sorting purposes.
      evalutedRoleByRowId[row.rowId] = role;
    }

    if (moleculeId && (role === ChemistryReactionRole.REACTANT || role === ChemistryReactionRole.PRODUCT)) {
      existingRowsByKey[makeRowKey(moleculeId, role)] = row;
    } else if (moleculeId != null || role != null) {
      // Slight hack: tables initially have a single empty row but we want to remove it when populating it.
      // As a heuristic, skip any rows that have neither a moleculeId nor a role.
      preservedRows.push(row);
    }
  }

  const resultRows: Array<StoichiometryTableRow> = [];
  let rowIndex = 0;

  const processComponents = (components: Array<ReactionComponent>, role: ChemistryReactionRole): void => {
    for (const component of components) {
      const existingRow = existingRowsByKey[makeRowKey(component.moleculeId, role)];

      if (existingRow) {
        resultRows.push(existingRow);
      } else {
        const blankCells = getBlankRow(rowIndex, columns, null);
        const cells = reactUpdate(blankCells, {
          [compoundColumnIndex]: {
            text: {$set: component.name},
            typedValue: {
              $set: {
                type: BaseTypedValueType.BIOENTITY,
                bioentitySummary: {id: component.moleculeId, name: component.name},
              },
            },
          },
          [roleColumnIndex]: {
            text: {$set: CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME[role]},
            typedValue: {
              $set: {
                type: BaseTypedValueType.STRING,
                rawValue: CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME[role],
              },
            },
          },
        });
        const newRow: StoichiometryTableRow = {
          cells,
          rowId: uuidv4(),
          isFinalized: false,
          metadata: {},
          lastSyncedUpstreamValues: null,
        };
        resultRows.push(newRow);
        evalutedRoleByRowId[newRow.rowId] = role;
      }
      rowIndex++;
    }
  };

  processComponents(newReactants, ChemistryReactionRole.REACTANT);
  processComponents(newProducts, ChemistryReactionRole.PRODUCT);

  const outputRows = resultRows.concat(preservedRows);

  return sortedBy(outputRows, [
    (row: StoichiometryTableRow): number => {
      const role = evalutedRoleByRowId[row.rowId];
      if (role == null) {
        // Push rows without role to the end.
        return Infinity;
      } else {
        return CHEMISTRY_REACTION_ROLE_TO_SORT_ORDER[role];
      }
    },
  ]);
}

/**
 * A chemical sketcher rendered inside a Stoichiometry Structured Table. It can be used to populate the
 * table with reactants and products.
 */
function StoichiometryTableEmbeddedChemicalSketcher(props: StoichiometryTableCustomSlotProps): JSX.Element {
  const {structuredTable, computedTable, updateHandler} = props;
  // The sketcher is always expected to be rendered within an Entry so fileId should not be null
  const entryId = structuredTable.fileId!;
  const [isSketcherModalOpen, {open: openSketcherModal, close: closeSketcherModal}] = useDisclosure(false);
  const [error, setError] = React.useState<string | null>(null);
  const initialStructureWithFormatRef = useRef<StructureWithFormat>();
  const reactionMrvExternalFile = structuredTable.metadata.reactionMrvExternalFile;
  const isSyncingFromTableToDiagram = useSyncReactionComponentsFromTable(
    structuredTable,
    computedTable,
    updateHandler
  );
  const analyticsMetadata: AnalyticsMetadataForContext = useMemo(
    () => ({
      entryId,
      fileId: entryId,
      actionOrigin: ActionOrigin.NOTEBOOK,
    }),
    [entryId]
  );

  const handleChangeChemicalStructure = useCallback(() => {
    setError(null);
  }, []);

  const openSketcherModalWithStructureAndFormat = useCallback(
    (structureWithFormat: StructureWithFormat, actionMethod: ActionMethod) => {
      initialStructureWithFormatRef.current = structureWithFormat;
      Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.CHEMISTRY, {
        ...analyticsMetadata,
        modalType: AnalyticsModalType.REACTION_SKETCHER,
        actionMethod,
      });
      openSketcherModal();
    },
    [openSketcherModal, analyticsMetadata]
  );
  const entryFolderId = useStoreSelector(
    EntryStore,
    useCallback(
      (entryStore: EntryStore): string | undefined =>
        entryStore.getEditorState(entryId)?.entry?.folder?.api_identifier,
      [entryId]
    )
  );

  return (
    <>
      {isSketcherModalOpen && (
        <ReactionSketcherModal
          chemicalStructureError={error}
          initialStructureData={initialStructureWithFormatRef.current}
          onChangeChemicalStructure={handleChangeChemicalStructure}
          onClose={() => {
            Analytics.trackEventWithModule(
              COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL,
              ANALYTICS_MODULE.CHEMISTRY,
              {
                ...analyticsMetadata,
                modalType: AnalyticsModalType.REACTION_SKETCHER,
              }
            );
            closeSketcherModal();
          }}
          onSubmit={async (newMarvinStructure: string): Promise<void> => {
            Analytics.trackEventWithModule(
              COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL,
              ANALYTICS_MODULE.CHEMISTRY,
              {
                ...analyticsMetadata,
                modalType: AnalyticsModalType.REACTION_SKETCHER,
              }
            );
            try {
              await withLoadingScreen(
                handleReactionSketcherSubmit(
                  newMarvinStructure,
                  entryFolderId!,
                  structuredTable,
                  computedTable,
                  updateHandler
                )
              );
              closeSketcherModal();
            } catch (e: unknown) {
              if (net.isJqXHR(e) && e.responseJSON?.error) {
                setError(
                  'There was an error reading the structure. Make sure the structures drawn are valid'
                );
              } else {
                throw e;
              }
            }
          }}
        />
      )}

      <Container
        fluid={true}
        // Not a magic number - this is design-approved but all the rendering is flexible so this can be adjusted as needed.
        h="216px"
        p="12px"
        style={{
          backgroundColor: Color.WHITE,
          border: `1px solid ${Color.GRAY_250}`,
          borderTop: 'none',
        }}
      >
        {reactionMrvExternalFile != null ? (
          <ReactionSchemeContent
            externalFileId={reactionMrvExternalFile.id}
            externalFileUrl={reactionMrvExternalFile.url}
            entryId={entryId}
            openSketcherModalWithStructureAndFormat={openSketcherModalWithStructureAndFormat}
            isSyncing={isSyncingFromTableToDiagram}
          />
        ) : (
          <EmptySchemePlaceholder
            openSketcherModalWithStructureAndFormat={openSketcherModalWithStructureAndFormat}
          />
        )}
      </Container>
    </>
  );
}

export default memo(StoichiometryTableEmbeddedChemicalSketcher);
